import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { flattenDocBlocks } from "@/lib/document-builder/blocks/blockFlatten";
import { evaluateDocFormulaFields } from "@/lib/document-builder/formulas/docFormulaEval";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { applyVariableValuesToBlocks } from "@/lib/document-builder/variables/variableSubstitution";
import { resolveLogoSrc, revokeIfBlobUrl } from "@/lib/document-builder/pdf/pdfLogo";
import { requestDocRenderPreview } from "@/lib/document/docExportClient";

export type RenderDocumentPdfArgs = {
  documentId: string;
  versionId: string;
  documentTitle: string;
  versionNumber: number;
  brandName: string;
  tagline?: string | null;
  logoUrl: string | null;
  blocks: DocBlock[];
  assets: DocAsset[];
  variableValues: DocVariableValues;
  skipPreviewGate?: boolean;
};

export type RenderDocumentPdfResult = { ok: true; blob: Blob } | { ok: false; error: string };

async function resolveAssetImageSrcs(assets: DocAsset[]): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const asset of assets) {
    if (!asset.signed_url) continue;
    const src = await resolveLogoSrc(asset.signed_url);
    if (src) map[asset.id] = src;
  }
  return map;
}

function collectImageAssetIds(blocks: readonly DocBlock[]): Set<string> {
  const ids = new Set<string>();
  for (const block of flattenDocBlocks(blocks)) {
    if (block.type === "image" && block.asset_id) ids.add(block.asset_id);
  }
  return ids;
}

export async function renderDocumentPdfToBlob(
  args: RenderDocumentPdfArgs,
): Promise<RenderDocumentPdfResult> {
  if (!args.skipPreviewGate) {
    const gate = await requestDocRenderPreview({
      document_id: args.documentId,
      version_id: args.versionId,
      variable_values: args.variableValues,
    });
    if (!gate.ok) return { ok: false, error: gate.error };
  }

  const resolvedBlocks = applyVariableValuesToBlocks(args.blocks, args.variableValues);
  const formulaEvaluation = evaluateDocFormulaFields({
    blocks: args.blocks,
    variableValues: args.variableValues,
  });

  const logoSrc = await resolveLogoSrc(args.logoUrl);
  const neededAssetIds = collectImageAssetIds(args.blocks);
  const relevantAssets = args.assets.filter((a) => neededAssetIds.has(a.id));
  const assetSrcById = await resolveAssetImageSrcs(relevantAssets);
  const blobUrls = [...Object.values(assetSrcById), logoSrc].filter(
    (s): s is string => !!s && s.startsWith("blob:"),
  );

  try {
    const [{ pdf }, { DocumentPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("@/components/document-builder/pdf/DocumentPdf"),
    ]);

    const document = DocumentPdf({
      documentTitle: args.documentTitle,
      versionLabel: `v${args.versionNumber}`,
      brandName: args.brandName,
      tagline: args.tagline,
      logoSrc,
      generatedAt: new Date(),
      blocks: resolvedBlocks,
      variableValues: args.variableValues,
      formulaEvaluation,
      assetSrcById,
    });

    const blob = await pdf(document).toBlob();
    return { ok: true, blob };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Failed to render PDF";
    return { ok: false, error: message };
  } finally {
    for (const url of blobUrls) revokeIfBlobUrl(url);
  }
}
